import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import MediumPriorityIcon from '@mui/icons-material/PriorityHigh';
import HighPriorityIcon from '@mui/icons-material/Error';
import CriticalPriorityIcon from '@mui/icons-material/ErrorOutline';
import {
  setTasks,
  addTask,
  updateTask,
  deleteTask,
} from '../redux/slices/tasksSlice';
import { setUsers, setError } from '../redux/slices/usersSlice';
import { db } from '../firebaseConfig';
import {
  collection,
  query,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { useOutletContext } from 'react-router-dom';

const ItemTypes = {
  TASK: 'task',
};

const priorities = [
  { id: 'low', label: 'low', icon: LowPriorityIcon, color: 'success' },
  { id: 'medium', label: 'medium', icon: MediumPriorityIcon, color: 'warning' },
  { id: 'high', label: 'high', icon: HighPriorityIcon, color: 'error' },
  {
    id: 'critical',
    label: 'critical',
    icon: CriticalPriorityIcon,
    color: 'error',
  },
];

const priorityOrder = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const sortTasksByPriority = (tasks) => {
  return [...tasks].sort((a, b) => {
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });
};

const renderPriorityIcon = (priority) => {
  const priorityData = priorities.find((p) => p.id === priority);
  if (!priorityData) return null;
  const Icon = priorityData.icon;
  return (
    <Tooltip title={priorityData.label}>
      <Icon color={priorityData.color} />
    </Tooltip>
  );
};

const TaskCard = ({ task, index, moveTask, status, onEditTask }) => {
  const { t } = useTranslation();
  const { projects } = useSelector((state) => state.projects);
  const { users } = useSelector((state) => state.users);
  const dispatch = useDispatch();

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.TASK,
    item: { id: task.id, index, status },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const handleDeleteTask = async (taskId) => {
    try {
      await deleteDoc(doc(db, 'tasks', taskId));
      dispatch(deleteTask(taskId));
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  return (
    <div
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        marginBottom: '8px',
      }}
    >
      <Card
        onClick={(e) => {
          e.stopPropagation();
          onEditTask(task);
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{task.name}</Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {renderPriorityIcon(task.priority)}
              <Tooltip title={t('tasks.editTask')}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditTask(task);
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('app.delete')}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteTask(task.id);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {task.description}
          </Typography>
          {task.projectId && (
            <Typography variant="caption" color="text.secondary">
              {t('tasks.project')}:{' '}
              {projects.find((p) => p.id === task.projectId)?.name}
            </Typography>
          )}
          {task.assignedTo && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5 }}
            >
              {t('tasks.assignedTo')}:{' '}
              {users?.find((u) => u.id === task.assignedTo)?.displayName || '-'}
            </Typography>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const Column = ({ status, tasks, moveTask, onEditTask }) => {
  const { t } = useTranslation();
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.TASK,
    drop: (item) => {
      if (item.status !== status.id) {
        moveTask(item.id, status.id);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <Grid item xs={12} sm={6} md={3} width="24%">
      <Paper
        sx={{
          p: 2,
          height: 'calc(100vh - 100px)',
          overflow: 'auto',
          backgroundColor: isOver ? 'rgba(0, 0, 0, 0.05)' : 'white',
          transition: 'background-color 0.2s ease',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t(`tasks.statuses.${status.label}`)}
        </Typography>
        <div ref={drop} style={{ minHeight: '100px' }}>
          {sortTasksByPriority(
            tasks.filter((task) => task.status === status.id)
          ).map((task, index) => (
            <TaskCard
              key={task.id}
              task={task}
              index={index}
              moveTask={moveTask}
              status={status.id}
              onEditTask={onEditTask}
            />
          ))}
        </div>
      </Paper>
    </Grid>
  );
};

const TasksPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { tasks } = useSelector((state) => state.tasks);
  const { projects } = useSelector((state) => state.projects);
  const { users } = useSelector((state) => state.users);
  const { openTaskDialog, setOpenTaskDialog } = useOutletContext();
  const [selectedTask, setSelectedTask] = useState(null);

  const statuses = [
    { id: 'todo', label: 'todo' },
    { id: 'in_progress', label: 'in_progress' },
    { id: 'review', label: 'review' },
    { id: 'done', label: 'done' },
  ];
  const types = ['feature', 'bug', 'improvement', 'other'];

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'feature',
    status: 'todo',
    priority: 'medium',
    assignedTo: '',
    projectId: '',
  });

  // Constante pour le type de droppable
  const DROPPABLE_TYPE = 'TASK';

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, []);

  const fetchTasks = async () => {
    try {
      const tasksRef = collection(db, 'tasks');
      const q = query(tasksRef);
      const querySnapshot = await getDocs(q);
      const tasksData = querySnapshot.docs.map((doc) => {
        const data = doc.data();

        return {
          id: doc.id,
          ...data,
          status: data.status || 'todo',
          priority: data.priority || 'medium',
        };
      });

      dispatch(setTasks(tasksData));
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef);
      const querySnapshot = await getDocs(q);
      const usersData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Ajout des utilisateurs hardcodés
      const hardcodedUsers = [];

      // Fusion des utilisateurs de la base de données avec les utilisateurs hardcodés
      const allUsers = [...usersData, ...hardcodedUsers];
      dispatch(setUsers(allUsers));
    } catch (error) {
      console.error('Error fetching users:', error);
      dispatch(setError(error.message));
    }
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setFormData({
      name: task.name,
      description: task.description,
      type: task.type,
      status: task.status,
      priority: task.priority,
      assignedTo: task.assignedTo,
      projectId: task.projectId,
    });
    setOpenTaskDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenTaskDialog(false);
    setSelectedTask(null);
  };

  const handleSubmit = async () => {
    try {
      const taskData = {
        ...formData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: formData.status || 'todo',
      };

      if (selectedTask) {
        const taskRef = doc(db, 'tasks', selectedTask.id);
        await updateDoc(taskRef, taskData);
        dispatch(updateTask({ id: selectedTask.id, ...taskData }));
      } else {
        const docRef = await addDoc(collection(db, 'tasks'), taskData);
        dispatch(addTask({ id: docRef.id, ...taskData }));
      }

      handleCloseDialog();
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  const moveTask = async (taskId, newStatus) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.status === newStatus) return;

    const updatedTask = {
      ...task,
      status: newStatus,
      updatedAt: new Date().toISOString(),
    };

    try {
      await updateDoc(doc(db, 'tasks', taskId), updatedTask);
      dispatch(updateTask(updatedTask));
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ height: '100vh', overflow: 'hidden' }}>
        <Grid container spacing={2} sx={{ height: '100%', p: 2 }}>
          {statuses.map((status) => (
            <Column
              key={status.id}
              status={status}
              tasks={tasks}
              moveTask={moveTask}
              onEditTask={handleEditTask}
            />
          ))}
        </Grid>
      </Box>

      <Dialog
        open={openTaskDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedTask ? t('tasks.editTask') : t('tasks.createTask')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} width="100%">
              <TextField
                fullWidth
                label={t('tasks.name')}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} width="100%">
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('tasks.description')}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} width="100%">
              <FormControl fullWidth>
                <InputLabel>{t('tasks.type')}</InputLabel>
                <Select
                  value={formData.type}
                  label={t('tasks.type')}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                >
                  {types.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(`tasks.types.${type}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} width="100%">
              <FormControl fullWidth>
                <InputLabel>{t('tasks.status')}</InputLabel>
                <Select
                  value={formData.status}
                  label={t('tasks.status')}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                >
                  {statuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {t(`tasks.statuses.${status.label}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} width="100%">
              <FormControl fullWidth>
                <InputLabel>{t('tasks.priority')}</InputLabel>
                <Select
                  value={formData.priority}
                  label={t('tasks.priority')}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                >
                  {priorities.map((priority) => (
                    <MenuItem key={priority.id} value={priority.id}>
                      {t(`tasks.priorities.${priority.label}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} width="100%">
              <FormControl fullWidth>
                <InputLabel>{t('tasks.project')}</InputLabel>
                <Select
                  value={formData.projectId}
                  label={t('tasks.project')}
                  onChange={(e) =>
                    setFormData({ ...formData, projectId: e.target.value })
                  }
                >
                  <MenuItem value="">{t('app.none')}</MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} width="100%">
              <FormControl fullWidth>
                <InputLabel>{t('tasks.assignedTo')}</InputLabel>
                <Select
                  value={formData.assignedTo}
                  label={t('tasks.assignedTo')}
                  onChange={(e) =>
                    setFormData({ ...formData, assignedTo: e.target.value })
                  }
                >
                  <MenuItem value="">{t('app.none')}</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.displayName || user.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('app.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {t('app.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </DndProvider>
  );
};

export default TasksPage;
